import Phaser from 'phaser';
import {
  GAME_WIDTH,
  GAME_HEIGHT,
  PLAYER_BASE_RADIUS,
  GROWTH,
  SPEED_BOOST_MULT,
  SHIELD,
  SIZE_SPEED,
} from '../config/gameConfig.js';
import { Frames } from '../utils/atlas.js';
import { advanceMembranePhase, membraneScaleXY } from '../utils/membraneVisual.js';

export class Player {
  /**
   * @param {Phaser.Scene} scene
   * @param {number} x
   * @param {number} y
   */
  constructor(scene, x, y) {
    this.scene = scene;
    this.sprite = scene.add.image(x, y, 'game-atlas', Frames.PLAYER);
    this.sprite.setDepth(100);

    const fx = this.sprite.postFX;
    if (fx && typeof fx.addGlow === 'function') {
      fx.addGlow(0x66ffaa, 3.2, 0.35, false, 0.25, 10);
    }
    this.velocity = new Phaser.Math.Vector2(0, 0);
    this.growthStacks = 0;
    this.radius = PLAYER_BASE_RADIUS;
    /** @type {number} 0 … SHIELD.maxStages */
    this.shieldStages = SHIELD.startStages;
    this.speedBoostTimer = 0;
    this.invisTimer = 0;
    this.pulseTick = 0;
    this.membranePhase = Math.random() * Math.PI * 2;
    /** Power tint target when not flashing */
    this.desiredTint = 0xffffff;
    /** Seconds remaining showing white hit flash */
    this.hitFlashTimer = 0;
  }

  get x() {
    return this.sprite.x;
  }

  get y() {
    return this.sprite.y;
  }

  /** @param {number} dt */
  syncRadiusFromGrowth() {
    const r =
      PLAYER_BASE_RADIUS +
      Math.min(this.growthStacks, GROWTH.maxStacks) * GROWTH.radiusPerCollect;
    this.radius = Math.min(r, GROWTH.maxRadius);
  }

  /** Discrete pulse + flowing membrane squash/stretch */
  updateVisual(dt, timeScale) {
    this.syncRadiusFromGrowth();
    const baseScale = 0.52 + Math.min(this.radius, GROWTH.maxRadius) / (GROWTH.maxRadius * 1.15);
    this.pulseTick += dt * 18 * timeScale;
    const step = Math.floor(this.pulseTick) % 20;
    const w = (step / 20) * Math.PI * 2;
    const pulse = 1 + 0.055 * Math.sin(w);

    this.membranePhase = advanceMembranePhase(this.membranePhase, dt, timeScale * (1 + this.radius / 220));
    const { sx, sy } = membraneScaleXY(this.membranePhase, 0.088, 0.036);

    this.sprite.setScale(baseScale * pulse * sx, baseScale * pulse * sy);
    this.sprite.rotation =
      0.045 * Math.sin(this.membranePhase * 0.62 + this.pulseTick * 0.08);
  }

  /** @param {number} radius */
  sizeBasedSpeedMul(radius) {
    const r = Phaser.Math.Clamp(radius, PLAYER_BASE_RADIUS, GROWTH.maxRadius);
    const { breakpointLow: rl, breakpointMid: rm, mulLow, mulMid, mulHigh } = SIZE_SPEED;
    if (r <= rl) {
      const t = Phaser.Math.Clamp((r - PLAYER_BASE_RADIUS) / (rl - PLAYER_BASE_RADIUS), 0, 1);
      return Phaser.Math.Linear(1, mulLow, t);
    }
    if (r <= rm) {
      const t = (r - rl) / (rm - rl);
      return Phaser.Math.Linear(mulLow, mulMid, t);
    }
    const t = Phaser.Math.Clamp((r - rm) / (GROWTH.maxRadius - rm), 0, 1);
    return Phaser.Math.Linear(mulMid, mulHigh, t);
  }

  speedMultiplier() {
    const boosted = this.speedBoostTimer > 0 ? SPEED_BOOST_MULT : 1;
    return this.sizeBasedSpeedMul(this.radius) * boosted;
  }

  scoreMultiplier() {
    return 1 + Math.min(this.growthStacks, GROWTH.maxStacks) * GROWTH.scoreMultPerStack;
  }

  isShielded() {
    return this.shieldStages > 0;
  }

  isInvisible() {
    return this.invisTimer > 0;
  }

  /** @param {number} dt */
  tickPowerUps(dt) {
    if (this.hitFlashTimer > 0) {
      this.hitFlashTimer = Math.max(0, this.hitFlashTimer - dt);
    }
    if (this.speedBoostTimer > 0) this.speedBoostTimer -= dt;
    if (this.invisTimer > 0) this.invisTimer -= dt;

    let tint = 0xffffff;
    if (this.shieldStages > 0) tint = 0x99ffff;
    else if (this.invisTimer > 0) tint = 0xccaaee;
    else if (this.speedBoostTimer > 0) tint = 0xffdd88;
    this.desiredTint = tint;

    const alpha = this.invisTimer > 0 ? 0.55 : 1;
    this.sprite.setAlpha(alpha);

    if (this.hitFlashTimer > 0) {
      this.sprite.setTint(0xffffff);
    } else {
      this.sprite.setTint(this.desiredTint);
    }
  }

  flashHit(durationSec = 0.075) {
    this.hitFlashTimer = Math.max(this.hitFlashTimer, durationSec);
  }

  /** @param {number} dx flow toward right */
  reset(x, y) {
    this.sprite.setPosition(x, y);
    this.velocity.set(0, 0);
    this.growthStacks = 0;
    this.radius = PLAYER_BASE_RADIUS;
    this.shieldStages = SHIELD.startStages;
    this.speedBoostTimer = 0;
    this.invisTimer = 0;
    this.hitFlashTimer = 0;
    this.desiredTint = 0xffffff;
    this.sprite.setTint(0xffffff);
    this.sprite.setAlpha(1);
    this.pulseTick = 0;
    this.membranePhase = Math.random() * Math.PI * 2;
    this.sprite.rotation = 0;
    this.syncRadiusFromGrowth();
    this.updateVisual(0, 1);
  }

  clampInBounds() {
    const pad = this.radius + 4;
    this.sprite.x = Phaser.Math.Clamp(this.sprite.x, pad, GAME_WIDTH - pad);
    this.sprite.y = Phaser.Math.Clamp(this.sprite.y, pad, GAME_HEIGHT - pad);
  }

  applyGrowth() {
    this.growthStacks = Math.min(this.growthStacks + 1, GROWTH.maxStacks);
    this.syncRadiusFromGrowth();
  }

  /** Drop logical radius / HUD Mass by `radiusLoss` (clamped at PLAYER_BASE_RADIUS). */
  applyMassPenalty(radiusLoss) {
    const loss = Math.max(0, radiusLoss);
    const target = Math.max(PLAYER_BASE_RADIUS, this.radius - loss);
    const stacks = Math.floor(
      (target - PLAYER_BASE_RADIUS) / GROWTH.radiusPerCollect + 1e-9
    );
    this.growthStacks = Math.max(0, Math.min(stacks, GROWTH.maxStacks));
    this.syncRadiusFromGrowth();
  }

  /** @param {number} stages */
  addShieldStages(stages) {
    this.shieldStages = Math.min(SHIELD.maxStages, this.shieldStages + stages);
  }

  consumeShieldHit() {
    if (this.shieldStages > 0) this.shieldStages -= 1;
  }
}
